/**
 * ADR-0508: Unified HTTP Fetch Layer
 *
 * Wraps fetch() to provide:
 *  - Typed Result<T, HttpError> return (no throwing)
 *  - Automatic AbortController per (method, url) key — cancels previous
 *    in-flight request for the same endpoint before starting a new one
 *  - CSRF token auto-attached from <meta name="csrf-token"> on non-GET requests
 *  - http.cancelAll() for the router to call on navigation
 *
 * Usage:
 *   import { http } from "../http";
 *   const result = await http.get<User[]>("/api/participants/search?q=foo");
 *   if (!result.ok) { logger.warn("search:fetch", result.error); return; }
 *   render(result.value);
 *
 *   // window global (plain JS):
 *   const result = await window.WMUHttp.post("/api/submissions", formData);
 */

import type { Result } from "./types/result";
import type { ApiEnvelope } from "./types/api_envelope";

export type HttpError = {
  status: number;     // 0 = network error, -1 = unknown, 1 = aborted
  message: string;
  body: unknown;
};

export type HttpResponse<T> = {
  data: T;
  response: Response;
};

const inFlight = new Map<string, AbortController>();

function csrfToken(): string {
  return (
    document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.content ?? ""
  );
}

function mergeHeaders(
  base: Record<string, string>,
  extra?: HeadersInit,
): Headers {
  const merged = new Headers(base);
  if (!extra) {
    return merged;
  }
  const additional = new Headers(extra);
  additional.forEach((value, key) => {
    merged.set(key, value);
  });
  return merged;
}

async function requestDetailed<T>(
  method: string,
  url: string,
  body?: unknown,
  opts?: RequestInit,
): Promise<Result<HttpResponse<T>, HttpError>> {
  const key = `${method}:${url}`;
  const { headers: extraHeaders, ...restOpts } = opts || {};

  // Cancel any previous in-flight request for the same endpoint
  inFlight.get(key)?.abort();
  const ctrl = new AbortController();
  inFlight.set(key, ctrl);

  try {
    const headers: Record<string, string> = {};

    if (method !== "GET" && method !== "HEAD") {
      const token = csrfToken();
      if (token) headers["X-CSRF-Token"] = token;
    }

    let fetchBody: BodyInit | undefined;
    if (body !== undefined) {
      if (body instanceof FormData || body instanceof URLSearchParams) {
        fetchBody = body;
      } else {
        headers["Content-Type"] = "application/json";
        fetchBody = JSON.stringify(body);
      }
    }

    const res = await fetch(url, {
      method,
      headers: mergeHeaders(headers, extraHeaders),
      body: fetchBody,
      signal: ctrl.signal,
      ...restOpts,
    });

    inFlight.delete(key);

    let responseBody: unknown;
    const ct = res.headers.get("content-type") ?? "";
    if (ct.includes("application/json")) {
      responseBody = await res.json();
    } else {
      responseBody = await res.text();
    }

    if (!res.ok) {
      return {
        ok: false,
        error: { status: res.status, message: res.statusText, body: responseBody },
      };
    }

    return {
      ok: true,
      value: {
        data: responseBody as T,
        response: res,
      },
    };
  } catch (err) {
    inFlight.delete(key);

    if (err instanceof DOMException && err.name === "AbortError") {
      return { ok: false, error: { status: 1, message: "aborted", body: null } };
    }

    return {
      ok: false,
      error: { status: 0, message: String(err), body: null },
    };
  }
}

async function request<T>(
  method: string,
  url: string,
  body?: unknown,
  opts?: RequestInit,
): Promise<Result<T, HttpError>> {
  const result = await requestDetailed<T>(method, url, body, opts);
  if (!result.ok) {
    return result;
  }
  return {
    ok: true,
    value: result.value.data,
  };
}

// Re-export ApiEnvelope so callers can type their responses:
//   const result = await http.get<ApiEnvelope<MyData>>("/api/...");
export type { ApiEnvelope };

export type WMUHttpAPI = {
  get<T>(url: string, opts?: RequestInit): Promise<Result<T, HttpError>>;
  post<T>(url: string, body?: unknown, opts?: RequestInit): Promise<Result<T, HttpError>>;
  put<T>(url: string, body?: unknown, opts?: RequestInit): Promise<Result<T, HttpError>>;
  patch<T>(url: string, body?: unknown, opts?: RequestInit): Promise<Result<T, HttpError>>;
  del<T>(url: string, opts?: RequestInit): Promise<Result<T, HttpError>>;
  send<T>(method: string, url: string, body?: unknown, opts?: RequestInit): Promise<Result<HttpResponse<T>, HttpError>>;
  cancelAll(): void;
};

export const http: WMUHttpAPI = {
  get<T>(url: string, opts?: RequestInit) {
    return request<T>("GET", url, undefined, opts);
  },
  post<T>(url: string, body?: unknown, opts?: RequestInit) {
    return request<T>("POST", url, body, opts);
  },
  put<T>(url: string, body?: unknown, opts?: RequestInit) {
    return request<T>("PUT", url, body, opts);
  },
  patch<T>(url: string, body?: unknown, opts?: RequestInit) {
    return request<T>("PATCH", url, body, opts);
  },
  del<T>(url: string, opts?: RequestInit) {
    return request<T>("DELETE", url, undefined, opts);
  },
  send<T>(method: string, url: string, body?: unknown, opts?: RequestInit) {
    return requestDetailed<T>(method, url, body, opts);
  },
  cancelAll() {
    inFlight.forEach((ctrl) => ctrl.abort());
    inFlight.clear();
  },
};

window.WMUHttp = http;
